import { FC, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import {
  PROVIDER_LABELS,
  TPaymentProvider,
  TProviderKey,
  useGetPaymentProviders,
  useUpdatePaymentProvider,
} from "../../api/billingAdmin";

/**
 * Choose the payment provider for NEW subscriptions.
 *
 * The single most important thing this screen has to communicate is what the
 * switch does NOT do. It does not migrate anyone, does not cancel anything and
 * does not move existing customers' money: every subscription is billed for
 * life by the provider that created it, and the switch only decides where the
 * next checkout goes. An operator who believes otherwise will either avoid a
 * safe change or make a dangerous assumption about one — so the copy and the
 * confirmation dialog both say it explicitly rather than leaving it to be
 * inferred from a tooltip.
 */
export const BillingProviderTab: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { activeProvider, providers, isLoading } = useGetPaymentProviders();
  const [pending, setPending] = useState<TProviderKey | null>(null);
  const updateProvider = useUpdatePaymentProvider(() => setPending(null));

  const pendingProvider = providers.find((p) => p.key === pending) ?? null;
  const currentProvider = providers.find((p) => p.key === activeProvider) ?? null;

  if (isLoading) {
    return (
      <div className={classes.card}>
        <div className={classes.emptyState}>
          <CircularProgress size={28} />
        </div>
      </div>
    );
  }

  const capabilityLabels: Record<string, string> = {
    promo_codes: t("billing.capPromo", "Codes promo"),
    billing_portal: t("billing.capPortal", "Portail client"),
    hosted_invoices: t("billing.capInvoices", "Factures PDF"),
    quantity_updates: t("billing.capQuantity", "Modification du nombre de véhicules"),
  };

  const renderProvider = (provider: TPaymentProvider) => {
    const isActive = provider.key === activeProvider;
    // A provider with no keys cannot be selected. Disabling the control is
    // better than letting the request fail: the reason is visible before the
    // click rather than in a toast afterwards.
    const selectable = provider.configured && !isActive;

    return (
      <div
        key={provider.key}
        className={classes.planCard}
        style={{
          borderColor: isActive ? "#0898FF" : undefined,
          opacity: provider.configured ? 1 : 0.6,
        }}
      >
        <div className={classes.planCardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <Radio
              checked={isActive}
              disabled={!selectable}
              onChange={() => setPending(provider.key)}
              inputProps={{ "aria-label": PROVIDER_LABELS[provider.key] }}
            />
            <div style={{ minWidth: 0 }}>
              <h4 className={classes.planName}>{PROVIDER_LABELS[provider.key]}</h4>
              <span className={classes.planSlug}>{provider.key}</span>
            </div>
          </div>

          {isActive && (
            <Chip
              size="small"
              color="primary"
              label={t("billing.providerActive", "Actif")}
            />
          )}
        </div>

        {!provider.configured && (
          <div className={classes.warningBox} style={{ marginBottom: 0 }}>
            {t(
              "billing.providerNotConfigured",
              "Clés API ou secret de webhook manquants. Configurez ce prestataire dans le fichier .env avant de l'activer."
            )}
          </div>
        )}

        <div className={classes.priceRow} style={{ flexDirection: "column", alignItems: "stretch" }}>
          <span className={classes.priceCycle}>
            {t("billing.providerCapabilities", "Fonctionnalités prises en charge")}
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {Object.entries(provider.capabilities).map(([key, supported]) => (
              <Chip
                key={key}
                size="small"
                variant={supported ? "filled" : "outlined"}
                color={supported ? "success" : "default"}
                label={capabilityLabels[key] ?? key}
                // Struck through rather than hidden: an operator comparing the
                // two needs to see what they would give up, not a shorter list
                // whose omissions they have to notice.
                sx={supported ? undefined : { textDecoration: "line-through", opacity: 0.7 }}
              />
            ))}
          </div>
        </div>

        <div className={classes.priceRow}>
          <span className={classes.priceCycle}>
            {t("billing.providerLiveSubs", "Abonnements en cours")}
          </span>
          <span className={classes.priceValue}>{provider.live_subscriptions}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={classes.card}>
        <div className={classes.cardTitle}>
          <h3>{t("billing.providerSection", "Prestataire de paiement")}</h3>
        </div>

        <div className={classes.infoBox}>
          {t(
            "billing.providerScopeInfo",
            "Ce choix s'applique uniquement aux NOUVEAUX abonnements. Les abonnements existants continuent d'être gérés par le prestataire qui les a créés : ils ne sont ni migrés, ni annulés, ni modifiés."
          )}
        </div>

        <div className={classes.planGrid}>{providers.map(renderProvider)}</div>
      </div>

      {currentProvider && !currentProvider.capabilities.promo_codes && (
        <div className={classes.card}>
          <div className={classes.warningBox} style={{ marginBottom: 0 }}>
            {t(
              "billing.providerPromoDisabled",
              "Le prestataire actif ne prend pas en charge les codes promo. Le champ correspondant est masqué au paiement, et l'onglet « Codes promo » ne s'applique qu'aux abonnements Stripe existants."
            )}
          </div>
        </div>
      )}

      {/*
        Confirmation is deliberate. The action is one click, reversible, and
        affects revenue collection for every future customer — the class of
        change where a moment's pause is worth more than the friction costs.
      */}
      <Dialog
        open={!!pendingProvider}
        onClose={() => setPending(null)}
        className={classes.dialog}
      >
        <DialogTitle>
          {t("billing.providerConfirmTitle", "Changer de prestataire ?")}
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            {t("billing.providerConfirmBody", {
              defaultValue:
                "Les nouveaux abonnements seront encaissés via {{next}} au lieu de {{current}}.",
              next: pendingProvider ? PROVIDER_LABELS[pendingProvider.key] : "",
              current: PROVIDER_LABELS[activeProvider as TProviderKey],
            })}
          </p>

          {/* Named explicitly, with the count, so "existing customers are
              unaffected" is a concrete statement about real subscriptions
              rather than reassuring boilerplate. */}
          {!!currentProvider?.live_subscriptions && (
            <div className={classes.infoBox} style={{ marginBottom: 0 }}>
              {t("billing.providerConfirmExisting", {
                defaultValue:
                  "Les {{count}} abonnement(s) actifs sur {{current}} ne sont pas concernés : ils continuent d'être facturés, renouvelés et annulés via {{current}}.",
                count: currentProvider.live_subscriptions,
                current: PROVIDER_LABELS[activeProvider as TProviderKey],
              })}
            </div>
          )}

          {pendingProvider && !pendingProvider.capabilities.promo_codes && (
            <div className={classes.warningBox} style={{ marginBottom: 0 }}>
              {t(
                "billing.providerConfirmNoPromo",
                "Ce prestataire ne prend pas en charge les codes promo ni le portail client en libre-service. Ces options seront masquées au paiement."
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button variant="text" onClick={() => setPending(null)}>
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            disabled={updateProvider.isPending}
            onClick={() => pendingProvider && updateProvider.mutate(pendingProvider.key)}
            startIcon={
              updateProvider.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t("billing.providerConfirmAction", "Confirmer le changement")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BillingProviderTab;
